import React, { useEffect, useState } from "react";
import { usePolling } from "../lib/usePolling";
import { useFollows } from "../context/FollowsContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { TeamBadge } from "../components/TeamBadge";
import { Disclaimer } from "../components/Disclaimer";
import { Footer } from "../components/Footer";
import { LanguageSelector } from "../components/LanguageSelector";
import { CaretLeft, Lightning, ChartBar, Target, Broadcast, TennisBall, PushPin, ArrowsLeftRight } from "@phosphor-icons/react";

const bandColor = (band) =>
  band === "excellent" ? "#00E676" : band === "good" ? "#43D65A" : band === "warning" ? "#FB8C00" : "#E53935";

export default function PredictionDetail() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { sport, home, away } = useParams();
  const location = useLocation();
  const [p, setP] = useState(location.state?.pred || null);
  const [loading, setLoading] = useState(!location.state?.pred);
  const [h2h, setH2h] = useState(null);
  const [live, setLive] = useState(null);
  const { isFollowed, toggle: toggleFollowCtx } = useFollows();
  const [h2hSurfOnly, setH2hSurfOnly] = useState(false);

  // Chargement à l'ouverture du match et au changement de langue uniquement (jamais relancé par la réponse).
  useEffect(() => {
    const ctrl = new AbortController();
    if (sport === "tennis") {
      const h = decodeURIComponent(home), a = decodeURIComponent(away);
      const tourn = location.state?.pred?.competition;
      api.get(`/ext/predictions/tennis/${encodeURIComponent(h)}/${encodeURIComponent(a)}`,
        { params: { tournament: (tourn && tourn !== "Tennis") ? tourn : undefined }, signal: ctrl.signal })
        .then((r) => setP((prev) => {
          const d = r.data;
          if (!d) return prev;
          const comp = (prev?.competition && prev.competition !== "Tennis") ? prev.competition : d.competition;
          return { ...d, competition: comp };
        }))
        .catch(() => {})
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
      return () => ctrl.abort();
    }
    if (location.state?.pred) return () => ctrl.abort();
    api.get(`/ext/predictions/${sport}`, { signal: ctrl.signal })
      .then((r) => {
        const h = decodeURIComponent(home), a = decodeURIComponent(away);
        const found = (r.data.predictions || []).find(
          (x) => x.home?.name === h && x.away?.name === a
        );
        setP(found || null);
      })
      .catch(() => {})
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, home, away, i18n.language]);

  // Confrontations : dépendent des joueurs (et du tournoi), pas de l'objet prédiction.
  const h2hHome = p?.home?.name, h2hAway = p?.away?.name, h2hComp = p?.competition;
  useEffect(() => {
    if (sport !== "tennis" || !h2hHome || !h2hAway) return;
    const ctrl = new AbortController();
    api.get(`/tennis/h2h`, { params: { home: h2hHome, away: h2hAway, tournament: h2hComp || undefined }, signal: ctrl.signal })
      .then((r) => setH2h(r.data?.available ? r.data : null))
      .catch(() => { if (!ctrl.signal.aborted) setH2h(null); });
    return () => ctrl.abort();
  }, [sport, h2hHome, h2hAway, h2hComp]);

  // xG moyen des 5 derniers matchs (football) — chargé à la demande
  useEffect(() => {
    if (sport === "tennis" || !p?.home?.name || !p?.away?.name || p.xg_form) return;
    api.get(`/foot/xg-form`, { params: { home: p.home.name, away: p.away.name } })
      .then((r) => {
        if (r.data && (r.data.home || r.data.away)) setP((prev) => ({ ...prev, xg_form: r.data }));
      })
      .catch(() => {});
  }, [sport, p]);


  // Poll real-time score for tennis (api-tennis live feed)
  const liveOn = sport === "tennis" && !!p?.home?.name;
  usePolling((signal) => (!liveOn ? Promise.resolve() : api.get(`/tennis/live`, {
    params: { home: p.home.name, away: p.away.name, tournament: p.competition || undefined }, signal })
    .then((r) => { if (!signal.aborted) setLive(r.data?.available ? r.data : null); })
    .catch(() => {})), 20000, [sport, p?.home?.name, p?.away?.name]);

  // État « suivi » (tennis) : liste déjà chargée par FollowsContext, aucune nouvelle demande.
  const followed = sport === "tennis" && !!p?.home?.name && isFollowed("tennis", { home: p.home.name, away: p.away.name });
  const toggleFollow = () => toggleFollowCtx("tennis", { home: p.home.name, away: p.away.name, tournament: p.competition });

  if (loading) {
    return <div className="min-h-screen grain flex items-center justify-center"><div className="w-32 h-32 rounded-full skeleton" /></div>;
  }
  if (!p) {
    return (
      <div className="grain min-h-screen px-5 pt-6" data-testid="prediction-detail">
        <button data-testid="pred-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-white/50 mb-6"><CaretLeft size={16} weight="bold" /> {t("pred.back")}</button>
        <p className="text-center text-white/40 text-sm mt-16">{t("pred.unavailable")}</p>
      </div>
    );
  }

  const c = bandColor(p.band);
  const scenKeys = new Set((p.scenarios || []).map((s) => s.key));
  const oddsKeys = ["home", "draw", "away"].filter((k) => p.odds && p.odds[k] && scenKeys.has(k));
  const hasOdds = oddsKeys.length > 0;
  const liveData = live || p.live || null;
  const setScores = liveData?.set_scores || [];
  const pronostic = (() => {
    const hp = (p.scenarios || []).find((s) => s.key === "home")?.pct;
    const ap = (p.scenarios || []).find((s) => s.key === "away")?.pct;
    if (hp == null || ap == null) return null;
    const homeFav = hp >= ap;
    const favName = homeFav ? (p.home.name || p.home.short) : (p.away.name || p.away.short);
    const favPct = Math.max(hp, ap);
    const reasons = [];
    const fr = homeFav ? p.home.rank : p.away.rank;
    const orr = homeFav ? p.away.rank : p.home.rank;
    if (fr && orr && fr < orr) reasons.push(t("pred.reason_ranked", { fr, orr }));
    if (h2h && h2h.surface) {
      const rec = (s) => s && s[h2h.surface];
      const wr = (r) => (r && (r.won + r.lost)) ? r.won / (r.won + r.lost) : -1;
      const fSurf = rec(homeFav ? h2h.home_surface : h2h.away_surface);
      const oSurf = rec(homeFav ? h2h.away_surface : h2h.home_surface);
      const sName = { hard: t("pred.surf_on_hard"), clay: t("pred.surf_on_clay"), grass: t("pred.surf_on_grass") }[h2h.surface];
      if (wr(fSurf) >= 0 && wr(fSurf) > wr(oSurf)) reasons.push(t("pred.reason_stronger", { surface: sName }));
    }
    if (h2h && h2h.matches?.length) {
      const fw = homeFav ? h2h.home_wins : h2h.away_wins;
      const ow = homeFav ? h2h.away_wins : h2h.home_wins;
      if (fw > ow) reasons.push(t("pred.reason_h2h_lead", { fw, ow }));
    }
    return t("pred.fav_line", { name: favName, pct: favPct }) + (reasons.length ? ` : ${reasons.join(", ")}.` : ".");
  })();

  return (
    <div className="grain relative pb-8" data-testid="prediction-detail">
      <div className="px-5 pt-6">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="pred-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-white/50"><CaretLeft size={16} weight="bold" /> {t("pred.back")}</button>
          <LanguageSelector variant="header" />
        </div>

        {p.competition && <p className="text-center text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">{p.competition}</p>}

        {/* Teams + score */}
        <div className="hm-card p-5" data-testid="pred-header">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
              <TeamBadge team={p.home} size={54} />
              <span className="text-sm font-bold text-center leading-tight">{p.home.name}</span>
              {p.home.rank ? <span className="text-[10px] font-bold text-gold/80" data-testid="detail-rank-home">ATP/WTA #{p.home.rank}{p.home.points ? ` · ${p.home.points} pts` : ""}</span> : null}
            </div>
            <div className="flex flex-col items-center shrink-0">
              <span className="font-numeral font-black text-3xl" style={{ color: c }}>{p.hitmax_score}</span>
              <span className="text-[9px] uppercase tracking-wider text-white/40">HitMax Score</span>
            </div>
            <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
              <TeamBadge team={p.away} size={54} />
              <span className="text-sm font-bold text-center leading-tight">{p.away.name}</span>
              {p.away.rank ? <span className="text-[10px] font-bold text-gold/80" data-testid="detail-rank-away">ATP/WTA #{p.away.rank}{p.away.points ? ` · ${p.away.points} pts` : ""}</span> : null}
            </div>
          </div>
          {p.label && <p className="text-center text-[11px] font-semibold mt-3" style={{ color: c }}>{p.label}</p>}
        </div>

        {sport === "tennis" && pronostic && (
          <div className="hm-card p-3.5 mt-4" data-testid="pred-pronostic"
            style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(0,230,118,0.04))", border: "1px solid rgba(212,175,55,0.35)" }}>
            <div className="flex items-center gap-1.5 mb-1">
              <Target size={13} weight="fill" color="#D4AF37" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-white/50">{t("pred.pronostic_label")}</span>
            </div>
            <p className="text-[13px] font-semibold text-white/85 leading-snug">{pronostic}</p>
          </div>
        )}

        {sport === "tennis" && (
          <button data-testid="open-compare-btn"
            onClick={() => navigate(`/tennis/compare?a=${encodeURIComponent(p.home.name)}&b=${encodeURIComponent(p.away.name)}`)}
            className="w-full mt-3 flex items-center justify-center gap-1.5 text-[12px] font-bold py-2 rounded-lg"
            style={{ background: "rgba(212,175,55,0.14)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.35)" }}>
            <ArrowsLeftRight size={14} weight="bold" /> {t("pred.compare_players")}
          </button>
        )}
        {/* Live score (tennis real-time) */}
        {liveData && (
          <div className="hm-card p-4 mt-4" data-testid="pred-live"
            style={{ background: liveData.live ? "rgba(0,230,118,0.06)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${liveData.live ? "rgba(0,230,118,0.4)" : "rgba(255,255,255,0.08)"}` }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Broadcast size={14} weight="fill" color={liveData.live ? "#00E676" : "#9AA0A6"} />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: liveData.live ? "#00E676" : "rgba(255,255,255,0.5)" }}>
                  {liveData.live ? t("live.badge") : (liveData.winner ? t("live.finished") : t("live.score"))}
                </span>
                {liveData.live && <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />}
              </div>
              <span className="text-[10px] text-white/45 truncate max-w-[55%] text-right">
                {[liveData.round, liveData.status].filter(Boolean).join(" · ")}
              </span>
            </div>
            <button data-testid="follow-toggle" onClick={toggleFollow}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 mb-2 text-[11px] font-bold transition-colors"
              style={followed
                ? { background: "rgba(212,175,55,0.16)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }
                : { background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <PushPin size={13} weight={followed ? "fill" : "regular"} />
              {followed ? t("live.following") : t("live.follow")}
            </button>
            {[{ n: p.home.name, side: "home" }, { n: p.away.name, side: "away" }].map((row) => (
              <div key={row.side} className="flex items-center justify-between py-1.5" data-testid={`live-row-${row.side}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {liveData.serving === row.side && liveData.live && (
                    <TennisBall size={13} weight="fill" color="#D4AF37" data-testid={`live-serve-${row.side}`} />
                  )}
                  <span className={`text-sm truncate ${liveData.winner === row.side ? "font-black text-perf-excellent" : "font-semibold text-white/85"}`}>{row.n}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {setScores.map((s, i) => (
                    <span key={i} className="font-numeral font-bold text-sm w-5 text-center text-white/75">{s[row.side]}</span>
                  ))}
                  {liveData.live && liveData.game && (
                    <span className="font-numeral font-black text-sm w-7 text-center rounded-md px-1"
                      style={{ background: "rgba(212,175,55,0.16)", color: "#D4AF37" }}>{liveData.game[row.side]}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live stats (tennis) */}
        {liveData?.stats?.length > 0 && (
          <div className="hm-card p-4 mt-4" data-testid="pred-stats">
            <div className="flex items-center gap-1.5 mb-3">
              <ChartBar size={14} weight="fill" color="#00E676" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t("live.stats_title")}</p>
            </div>
            <div className="space-y-2.5">
              {liveData.stats.map((s) => (
                <div key={s.key} data-testid={`stat-${s.key}`} className="grid grid-cols-3 items-center gap-2">
                  <span className="font-numeral font-bold text-sm text-left text-white/85">{s.home ?? "–"}</span>
                  <span className="text-[10px] text-center text-white/45 uppercase tracking-wide">{t(`live.stat.${s.key}`)}</span>
                  <span className="font-numeral font-bold text-sm text-right text-white/85">{s.away ?? "–"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Point-by-point — last game (tennis live) */}
        {liveData?.last_game?.points?.length > 0 && (
          <div className="hm-card p-4 mt-4" data-testid="pred-pbp">
            <div className="flex items-center gap-1.5 mb-3">
              <TennisBall size={14} weight="fill" color="#D4AF37" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                {t("live.pbp")} · {[liveData.last_game.set, liveData.last_game.game ? `${t("live.game")} ${liveData.last_game.game}` : ""].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1" data-testid="pbp-points">
              {liveData.last_game.points.map((pt, i) => {
                const flag = pt.match_point ? "MP" : pt.set_point ? "SP" : pt.break_point ? "BP" : "";
                const fc = pt.match_point ? "#E53935" : pt.set_point ? "#FB8C00" : pt.break_point ? "#00E676" : null;
                return (
                  <div key={i} className="shrink-0 flex flex-col items-center gap-1">
                    <span className="font-numeral font-bold text-[13px] rounded-md px-2 py-1 bg-white/6 text-white/85 whitespace-nowrap">
                      {pt.home}<span className="text-white/30 mx-0.5">-</span>{pt.away}
                    </span>
                    {flag ? <span className="text-[8px] font-black" style={{ color: fc }}>{flag}</span> : <span className="text-[8px]">&nbsp;</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {p.recommendation?.text && (
          <div className="hm-card p-4 mt-4 flex items-center justify-between" data-testid="pred-reco"
            style={{ background: `${c}14`, border: `1px solid ${c}55` }}>
            <div className="flex items-center gap-2">
              <Lightning size={18} weight="fill" color={c} />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40">{t("pred.reco")}</p>
                <p className="text-sm font-black" style={{ color: c }}>{p.recommendation.text}</p>
              </div>
            </div>
            <span className="font-numeral font-black text-xl" style={{ color: c }}>{p.recommendation.prob}%</span>
          </div>
        )}

        {/* Scenarios */}
        {p.scenarios?.length > 0 && (
          <div className="hm-card p-4 mt-4" data-testid="pred-scenarios">
            <div className="flex items-center gap-1.5 mb-3">
              <ChartBar size={14} weight="fill" color="#D4AF37" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t("pred.scenarios")}</p>
            </div>
            {p.scenarios.map((s) => (
              <div key={s.key} className="mb-2.5 last:mb-0">
                <div className="flex justify-between text-[12px] mb-1">
                  <span className="text-white/70 truncate pr-2">{s.label}</span>
                  <span className="font-numeral font-bold text-white">{s.pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${s.pct}%`, background: s.key === "draw" ? "#8A8A8A" : c }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Odds + likely score */}
        {(hasOdds || p.likely_score) && (
          <div className="hm-card p-4 mt-4 grid grid-cols-2 gap-3" data-testid="pred-odds">
            {hasOdds && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">{t("pred.odds_est")}</p>
                <div className="flex gap-2 text-center">
                  {oddsKeys.map((k) => (
                    <div key={k} className="flex-1 bg-white/5 rounded-lg py-1.5">
                      <p className="text-[9px] text-white/40 uppercase">{k === "home" ? "1" : k === "draw" ? "N" : "2"}</p>
                      <p className="font-numeral font-bold text-sm text-gold">{p.odds[k]}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {p.likely_score && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">{t("pred.likely_score")}</p>
                <div className="flex items-center gap-1.5 bg-white/5 rounded-lg py-1.5 px-3 justify-center">
                  <Target size={14} weight="fill" color="#D4AF37" />
                  <span className="font-numeral font-black text-lg">{p.likely_score}</span>
                  {p.likely_score_prob != null && (
                    <span className="font-numeral text-xs text-white/40">({p.likely_score_prob}%)</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* xG (buts attendus) */}
        {p.xg && (
          <div className="hm-card p-4 mt-4" data-testid="pred-xg">
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Buts attendus (xG)</p>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold truncate flex-1">{p.home.name}</span>
              <span className="font-numeral font-black text-perf-excellent" data-testid="pred-xg-home">{p.xg.home}</span>
              <span className="text-white/30 text-xs">—</span>
              <span className="font-numeral font-black text-gold" data-testid="pred-xg-away">{p.xg.away}</span>
              <span className="text-xs font-semibold truncate flex-1 text-right">{p.away.name}</span>
            </div>
          </div>
        )}

        {/* xG forme (5 derniers matchs) */}
        {p.xg_form && (p.xg_form.home || p.xg_form.away) && (
          <div className="hm-card p-4 mt-4" data-testid="pred-xg-form">
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-3">xG moyen récent (données officielles)</p>
            {[["home", p.home], ["away", p.away]].map(([side, team]) => {
              const xf = p.xg_form[side];
              if (!xf) return null;
              return (
                <div key={side} className="flex items-center gap-3 mb-2 last:mb-0" data-testid={`xg-form-${side}`}>
                  <span className="text-xs font-semibold truncate flex-1">
                    {team.name}
                    <span className="text-white/30 font-normal ml-1">· {xf.n} match{xf.n > 1 ? "s" : ""}</span>
                  </span>
                  <span className="text-[11px] text-white/50">marqués</span>
                  <span className="font-numeral font-black text-perf-excellent w-9 text-right">{xf.for ?? "—"}</span>
                  <span className="text-[11px] text-white/50">encaissés</span>
                  <span className="font-numeral font-black text-perf-danger w-9 text-right">{xf.against ?? "—"}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Surface & per-surface records (tennis) */}
        {h2h && (h2h.home_surface || h2h.away_surface) && (
          <div className="hm-card p-4 mt-4" data-testid="pred-surface">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <TennisBall size={14} weight="fill" color="#D4AF37" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t("pred.surface_terrain")}</p>
              </div>
              {h2h.surface && (
                <span data-testid="surface-badge" className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(212,175,55,0.16)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }}>
                  {{ hard: t("pred.surf_dur"), clay: t("pred.surf_terre_battue"), grass: t("pred.surf_gazon") }[h2h.surface]}
                </span>
              )}
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 mb-2 text-[10px] text-white/40">
              <span className="truncate">{p.home.short || p.home.name}</span>
              <span className="text-center px-2">{t("pred.record", { season: h2h.home_surface?.season || h2h.away_surface?.season || "" })}</span>
              <span className="text-right truncate">{p.away.short || p.away.name}</span>
            </div>
            {[["hard", t("pred.surf_dur")], ["clay", t("pred.surf_terre")], ["grass", t("pred.surf_gazon")]].map(([k, label]) => {
              const hs = h2h.home_surface?.[k], as = h2h.away_surface?.[k];
              const active = h2h.surface === k;
              const rec = (r) => r ? `${r.won}-${r.lost}` : "–";
              const pctv = (r) => r && (r.won + r.lost) ? Math.round((r.won / (r.won + r.lost)) * 100) : null;
              const hp = pctv(hs), ap = pctv(as);
              return (
                <div key={k} data-testid={`surface-row-${k}`}
                  className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-2 rounded-lg px-2 mb-1"
                  style={active ? { background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" } : {}}>
                  <div className="text-left">
                    <span className="font-numeral font-bold text-sm text-white/85">{rec(hs)}</span>
                    {hp !== null && <span className="text-[10px] text-white/40 ml-1">({hp}%)</span>}
                  </div>
                  <span className={`text-[11px] text-center px-2 font-semibold ${active ? "text-gold" : "text-white/45"}`}>{label}</span>
                  <div className="text-right">
                    {ap !== null && <span className="text-[10px] text-white/40 mr-1">({ap}%)</span>}
                    <span className="font-numeral font-bold text-sm text-white/85">{rec(as)}</span>
                  </div>
                </div>
              );
            })}
            {h2h.surface && (h2h.home_surface?.[h2h.surface] || h2h.away_surface?.[h2h.surface]) && (
              <p className="text-[10px] text-white/45 text-center mt-2" data-testid="surface-note">
                {(() => {
                  const s = h2h.surface;
                  const hp = h2h.home_surface?.[s], ap = h2h.away_surface?.[s];
                  const pv = (r) => r && (r.won + r.lost) ? r.won / (r.won + r.lost) : -1;
                  const label = { hard: t("pred.surf_on_hard"), clay: t("pred.surf_on_clay"), grass: t("pred.surf_on_grass") }[s];
                  if (pv(hp) === pv(ap)) return t("pred.level_equal", { surface: label });
                  const better = pv(hp) > pv(ap) ? (p.home.short || p.home.name) : (p.away.short || p.away.name);
                  return t("pred.advantage", { name: better, surface: label });
                })()}
              </p>
            )}
          </div>
        )}

        {/* Recent form (tennis) */}
        {h2h && (h2h.home_form?.length > 0 || h2h.away_form?.length > 0) && (
          <div className="hm-card p-4 mt-4" data-testid="pred-form">
            <div className="flex items-center gap-1.5 mb-3">
              <Lightning size={14} weight="fill" color="#D4AF37" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t("pred.form")}</p>
            </div>
            {[{ name: p.home.name, form: h2h.home_form }, { name: p.away.name, form: h2h.away_form }].map((pl, idx) => (
              <div key={idx} className={idx === 0 ? "mb-3" : ""} data-testid={`form-player-${idx}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] font-bold text-white/80 truncate pr-2">{pl.name}</span>
                  <div className="flex gap-1 shrink-0">
                    {(pl.form || []).map((f, i) => (
                      <span key={i}
                        className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black"
                        style={{ background: f.result === "W" ? "rgba(0,230,118,0.18)" : "rgba(229,57,53,0.18)",
                          color: f.result === "W" ? "#00E676" : "#E53935",
                          border: `1px solid ${f.result === "W" ? "rgba(0,230,118,0.45)" : "rgba(229,57,53,0.45)"}` }}>
                        {f.result}
                      </span>
                    ))}
                  </div>
                </div>
                {(pl.form || []).length > 0 && (
                  <p className="text-[10px] text-white/35 truncate">
                    {t("pred.last_match", { result: pl.form[0].result === "W" ? t("pred.result_win") : t("pred.result_loss"), opp: pl.form[0].opponent, score: pl.form[0].score })}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Head-to-head (tennis) */}
        {h2h && (h2h.matches?.length > 0) && (() => {
          const surfLabel = { hard: t("pred.surf_dur"), clay: t("pred.surf_terre"), grass: t("pred.surf_gazon") }[h2h.surface];
          const surfMatches = h2h.surface ? h2h.matches.filter((m) => m.surface === h2h.surface) : [];
          const showToggle = h2h.surface && surfMatches.length > 0 && surfMatches.length < h2h.matches.length;
          const noSurfDuel = h2h.surface && surfMatches.length === 0;
          const surfName = { hard: t("pred.surf_hard"), clay: t("pred.surf_clay"), grass: t("pred.surf_grass") }[h2h.surface];
          const list = h2hSurfOnly && showToggle ? surfMatches : h2h.matches;
          const hw = list.filter((m) => m.winner === "home").length;
          const aw = list.filter((m) => m.winner === "away").length;
          return (
          <div className="hm-card p-4 mt-4" data-testid="pred-h2h">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <ChartBar size={14} weight="fill" color="#D4AF37" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{t("pred.h2h")}</p>
              </div>
              {showToggle && (
                <div className="flex gap-1 p-0.5 rounded-lg bg-white/5" data-testid="h2h-surface-toggle">
                  <button data-testid="h2h-filter-all" onClick={() => setH2hSurfOnly(false)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-md ${!h2hSurfOnly ? "bg-gold text-black" : "text-white/50"}`}>{t("pred.all")}</button>
                  <button data-testid="h2h-filter-surface" onClick={() => setH2hSurfOnly(true)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-md ${h2hSurfOnly ? "bg-gold text-black" : "text-white/50"}`}>{surfLabel}</button>
                </div>
              )}
            </div>
            {noSurfDuel && (
              <p className="text-[10px] text-white/40 text-center mb-2" data-testid="h2h-no-surface">
                {t("pred.no_duel", { surface: surfName })}
              </p>
            )}
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-center flex-1 min-w-0">
                <p className="font-numeral font-black text-2xl" style={{ color: c }}>{hw}</p>
                <p className="text-[10px] text-white/50 truncate">{p.home.name}</p>
              </div>
              <span className="font-numeral font-black text-white/30 text-sm px-2">—</span>
              <div className="text-center flex-1 min-w-0">
                <p className="font-numeral font-black text-2xl text-white/80">{aw}</p>
                <p className="text-[10px] text-white/50 truncate">{p.away.name}</p>
              </div>
            </div>
            <ul className="space-y-2">
              {list.map((m, i) => (
                <li key={i} className="flex items-center justify-between text-[11px] bg-white/5 rounded-lg px-2.5 py-1.5" data-testid={`h2h-match-${i}`}>
                  <div className="min-w-0 flex-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: m.winner === "home" ? "#00E676" : m.winner === "away" ? "#D4AF37" : "rgba(255,255,255,0.2)" }} />
                    <div className="min-w-0">
                      <p className="text-white/75 truncate">{p.home.short || p.home.name} vs {p.away.short || p.away.name}</p>
                      <p className="text-white/35 text-[10px] truncate">{[m.tournament, m.round, m.date].filter(Boolean).join(" · ")}</p>
                    </div>
                  </div>
                  <span className="font-numeral font-bold text-gold ml-2 shrink-0">{m.score}</span>
                </li>
              ))}
            </ul>
          </div>
          );
        })()}

        {/* Reasoning */}
        {p.reasoning?.length > 0 && (
          <div className="hm-card p-4 mt-4" data-testid="pred-reasoning">
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-3">{t("pred.detailed_analysis")}</p>
            <ul className="space-y-2.5">
              {(Array.isArray(p.reasoning) ? p.reasoning : [p.reasoning]).map((r, i) => (
                <li key={i} className="flex gap-2 text-[13px] text-white/75 leading-snug">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c }} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Disclaimer className="mt-4" />
        <Footer />
      </div>
    </div>
  );
}
